import React, { useEffect, useRef, useState } from "react";
import Logo from "../assets/images/fugus-logo.webp";
import { Link, NavLink, useLocation } from "react-router-dom";
import MobileDrawer from "./MobileDrawer";
import LogoutConfirmation from "../pages/shared/logoutConfirmation/LogoutConfirmation";
import api from "../shared/utils/request";
import SearchResults from "../pages/shared/searchResult/SearchResults";

const desktopProfileOptions = [
  { id: 1, label: "My Account", to: "/my-account", icon: "ph ph-user" },
  { id: 2, label: "My Orders", to: "/my-orders", icon: "ph ph-package" },
  { id: 4, label: "Address Book", to: "/address-book", icon: "ph ph-map-pin" },
  { id: 4, label: "Logout", to: "#", icon: "ph ph-sign-out" },
];

const headerAnnouncements = [
  "Fresh catch delivered in 90 minutes",
  "Free delivery on orders above Rs. 499",
  "Weekend deal: 15% off on premium seafood",
  "Cleaned and ready-to-cook fish available",
];

const Search = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDrawerOverlay, setActiveDrawerOverlay] = useState("");
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isDesktopProfileDropdownOpen, setIsDesktopProfileDropdownOpen] =
    useState(false);
  const desktopProfileRef = useRef(null);
  const location = useLocation();
  const isToken = localStorage.getItem("token");

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isDesktopSearchLoading, setIsDesktopSearchLoading] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [mobileSearchResults, setMobileSearchResults] = useState([]);
  const [showMobileSearchResults, setShowMobileSearchResults] = useState(false);
  const [isMobileSearchLoading, setIsMobileSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const desktopSearchRequestIdRef = useRef(0);
  const mobileSearchRequestIdRef = useRef(0);

  const fetchSearchResults = async (query) => {
    const data = await api.post("customer/getProducts", {
      limit: 25,
      offset: 0,
      search: query,
      sort_by: "id",
      sort_order: "desc",
    });
    const variants = data?.data || [];
    return variants.map((item) => ({
      id: item?.product?.id || item?.id,
      slug: item?.product?.slug,
      product_variants: [
        {
          title: item?.title,
          sale_price: item?.sale_price,
          product_image: item?.product_image,
        },
      ],
    }));
  };

  const handleLogout = () => {
    localStorage.clear();
    setShowLogoutModal(false);
    window.location.href = "/";
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDrawerOverlay("");
    setIsProfileDropdownOpen(false);
    setIsDesktopProfileDropdownOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2) {
      desktopSearchRequestIdRef.current += 1;
      setSearchResults([]);
      setShowSearchResults(false);
      setIsDesktopSearchLoading(false);
      return undefined;
    }

    setIsDesktopSearchLoading(true);
    setShowSearchResults(true);
    desktopSearchRequestIdRef.current += 1;
    const currentRequestId = desktopSearchRequestIdRef.current;
    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await fetchSearchResults(query);
        if (currentRequestId !== desktopSearchRequestIdRef.current) return;
        setSearchResults(results);
      } catch (error) {
        if (currentRequestId !== desktopSearchRequestIdRef.current) return;
        console.error("Search failed:", error);
        setSearchResults([]);
      } finally {
        if (currentRequestId !== desktopSearchRequestIdRef.current) return;
        setIsDesktopSearchLoading(false);
      }
    }, 220);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery]);

  useEffect(() => {
    const query = mobileSearchQuery.trim();
    if (query.length < 2) {
      mobileSearchRequestIdRef.current += 1;
      setMobileSearchResults([]);
      setShowMobileSearchResults(false);
      setIsMobileSearchLoading(false);
      return undefined;
    }

    setIsMobileSearchLoading(true);
    setShowMobileSearchResults(true);
    mobileSearchRequestIdRef.current += 1;
    const currentRequestId = mobileSearchRequestIdRef.current;
    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await fetchSearchResults(query);
        if (currentRequestId !== mobileSearchRequestIdRef.current) return;
        setMobileSearchResults(results);
      } catch (error) {
        if (currentRequestId !== mobileSearchRequestIdRef.current) return;
        console.error("Search failed:", error);
        setMobileSearchResults([]);
      } finally {
        if (currentRequestId !== mobileSearchRequestIdRef.current) return;
        setIsMobileSearchLoading(false);
      }
    }, 220);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mobileSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopProfileRef.current &&
        !desktopProfileRef.current.contains(event.target)
      ) {
        setIsDesktopProfileDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(event.target)
      ) {
        setShowMobileSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <header className="mobile-app-header" aria-label="Mobile header">
        <div className="mobile-app-header__top">
          <button
            type="button"
            className="mobile-app-header__icon-btn"
            aria-label="Open menu"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <i className="ph ph-list" />
          </button>
          <Link to="/" className="mobile-app-header__logo-link">
            <img src={Logo} alt="Fugu's" className="mobile-app-header__logo" style={{ maxWidth: "165px" }} />
          </Link>
          <Link
            to="/cart"
            className="mobile-app-header__icon-btn"
            aria-label="Open cart"
          >
            <i className="ph ph-handbag" />
          </Link>
        </div>
        <form
          className="mobile-app-header__search"
          onSubmit={(event) => event.preventDefault()}
          ref={mobileSearchRef}
        >
          <span className="mobile-app-header__search-icon" aria-hidden="true">
            <i className="ph ph-magnifying-glass" />
          </span>
          <input
            type="text"
            className="mobile-app-header__search-input"
            placeholder="Search fish, prawns, crab..."
            value={mobileSearchQuery}
            onChange={(e) => setMobileSearchQuery(e.target.value)}
            onFocus={() => mobileSearchQuery.length >= 2 && setShowMobileSearchResults(true)}
          />
          {mobileSearchQuery && (
            <button
              type="button"
              className="mobile-app-header__search-clear"
              aria-label="Clear search"
              onClick={() => {
                setMobileSearchQuery("");
                setMobileSearchResults([]);
                setShowMobileSearchResults(false);
              }}
            >
              <i className="ph ph-x" />
            </button>
          )}
          {showMobileSearchResults && (
            <SearchResults
              results={mobileSearchResults}
              query={mobileSearchQuery}
              isLoading={isMobileSearchLoading}
              onResultClick={() => {
                setMobileSearchQuery("");
                setShowMobileSearchResults(false);
              }}
            />
          )}
        </form>
      </header>
      <MobileDrawer
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={() => setIsMobileMenuOpen(false)}
        activeDrawerOverlay={activeDrawerOverlay}
        setActiveDrawerOverlay={setActiveDrawerOverlay}
        isProfileDropdownOpen={isProfileDropdownOpen}
        setIsProfileDropdownOpen={setIsProfileDropdownOpen}
      />
      <div className="desktop-header-wrap">
        <div className="header-top bg-main-600 flex-between">
          <div className="container container-lg">
            <div className="header-top-marquee" aria-label="Top announcements">
              <div className="header-top-marquee__track">
                {[...headerAnnouncements, ...headerAnnouncements].map(
                  (message, index) => (
                    <span
                      className="header-top-marquee__item"
                      key={`${message}-${index}`}
                    >
                      {message}
                    </span>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
        <header className="header-middle border-bottom border-gray-100">
          <div className="container container-lg">
            <nav className="header-inner flex-between gap-8">
              {/* Logo Start */}
              <div className="logo">
                <Link to={"/"} className="link">
                  <img src={Logo} alt="Logo" />
                </Link>
              </div>
              {/* Logo End  */}
              {/* form location Start */}
              <form
                action="#"
                className="flex-align flex-wrap form-location-wrapper max-w-840 w-100"
              >
                <div
                  className="search-category select-style-one d-flex select-border-end-0 search-form d-sm-flex d-none text-heading-two text-sm w-100"
                  ref={searchRef}
                >
                  <div className="search-form__wrapper search-form__wrapper--cool position-relative border-half-start flex-grow-1">
                    <span className="search-form__leading-icon" aria-hidden="true">
                      <i className="ph ph-magnifying-glass" />
                    </span>
                    <input
                      type="text"
                      className="common-input search-form__input border-neutral-40 py-18 ps-16 pe-76 rounded-0 rounded-end pe-44 placeholder-italic placeholder-text-sm border-start-0"
                      placeholder="Search fish, seafood, cuts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="search-form__clear-btn"
                        aria-label="Clear search"
                        onClick={() => {
                          setSearchQuery("");
                          setSearchResults([]);
                          setShowSearchResults(false);
                        }}
                      >
                        <i className="ph ph-x" />
                      </button>
                    )}
                    {showSearchResults && (
                      <SearchResults
                        results={searchResults}
                        query={searchQuery}
                        isLoading={isDesktopSearchLoading}
                        onResultClick={() => {
                          setSearchQuery("");
                          setShowSearchResults(false);
                        }}
                      />
                    )}
                  </div>
                </div>
              </form>
              {/* form location start */}
              {/* Header Middle Right start */}
              <div className="header-right flex-align flex-shrink-0">
                <div className="flex-align gap-20 header-action-list">
                  <button
                    type="button"
                    className="search-icon flex-align d-lg-none d-flex gap-4 item-hover"
                  >
                    <span className="text-2xl text-gray-700 d-flex position-relative item-hover__text">
                      <i className="ph ph-magnifying-glass" />
                    </span>
                  </button>
                  {isToken ? (
                    <div
                      className="header-action-dropdown d-none d-lg-flex"
                      ref={desktopProfileRef}
                    >
                      <button
                        type="button"
                        className="header-action-btn item-hover"
                        onClick={() =>
                          setIsDesktopProfileDropdownOpen((prev) => !prev)
                        }
                        aria-expanded={isDesktopProfileDropdownOpen}
                        aria-label="Profile options"
                      >
                        <span className="header-action-btn__icon">
                          <i className="ph ph-user-circle" />
                        </span>
                        <span className="header-action-btn__label">
                          Profile
                        </span>
                      </button>
                      <div
                        className={`header-action-dropdown__menu ${
                          isDesktopProfileDropdownOpen ? "is-open" : ""
                        }`}
                      >
                        {desktopProfileOptions.map((item) =>
                          item?.label === "Logout" ? (
                            <NavLink
                              href="#"
                              key={item.id}
                              className={() => `header-action-dropdown__link`}
                              onClick={() => {
                                setShowLogoutModal(true);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <i className={item.icon} aria-hidden="true" />
                              <span>{item.label}</span>
                            </NavLink>
                          ) : (
                            <NavLink
                              key={item.id}
                              to={item.to}
                              className={({ isActive }) =>
                                `header-action-dropdown__link${
                                  isActive ? " is-active" : ""
                                }`
                              }
                              onClick={() =>
                                setIsDesktopProfileDropdownOpen(false)
                              }
                            >
                              <i className={item.icon} aria-hidden="true" />
                              <span>{item.label}</span>
                            </NavLink>
                          ),
                        )}
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="header-action-btn item-hover d-none d-lg-flex"
                      onClick={() => {
                        window.showLoginModal?.();
                      }}
                    >
                      <span className="header-action-btn__icon">
                        <i className="ph ph-user-circle" />
                      </span>
                      <span className="header-action-btn__label">Profile</span>
                    </button>
                  )}
                  <Link
                    to={"/wishlist"}
                    className="header-action-btn item-hover"
                  >
                    <span className="header-action-btn__icon">
                      <i className="ph ph-heart-straight" />
                    </span>
                    <span className="header-action-btn__label">Wishlist</span>
                  </Link>
                  <Link to={"/cart"} className="header-action-btn item-hover">
                    <span className="header-action-btn__icon">
                      <i className="ph ph-shopping-cart-simple" />
                    </span>
                    <span className="header-action-btn__label">Cart</span>
                  </Link>
                </div>{" "}
              </div>
              {/* Header Middle Right End  */}
            </nav>
          </div>
        </header>
        <LogoutConfirmation
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogout}
        />
      </div>
    </>
  );
};

export default Search;
